import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from '@hello-pangea/dnd'
import { Box, useTheme } from '@mui/material'
import EventContextMenu from 'components/contextMenu/EventContextMenu'
import { useAppSettings } from 'components/providers/AppSettingsProvider'
import { useCalendar } from 'components/providers/CalendarProvider'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'
import dayjs, { Dayjs } from 'dayjs'
import { t } from 'i18next'
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef
} from 'react'
import React from 'react'
import { updateEventOnServer } from 'services/api/event'
import Database from 'services/db/Database'
import { localEventById, localEventByLocalId } from 'services/db/Queries/Event'
import LocalStorage from 'services/LocalStorage'
import { EventDoc, GroupDoc, UserSettingsDoc } from 'types/api/changes'
import { CalendarRange } from 'types/components/calendar'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import {
  buildEventsArrayForUIFromDBEventsWithRecurrentEvents,
  eventComparator
} from 'utils/api/events'
import { StartEndEvent } from 'utils/date'
import { eventIdForContainer, isEventRecurrenceValid } from 'utils/event'
import { isConvertedGroupId } from 'utils/groups'
import { getDefaultTimezone } from 'utils/timezone'
import {
  eventDisplayDate as listEventDate,
  eventEndTimestamp
} from 'utils/eventDates'
import { isGuest } from 'utils/user'

import dateToTimeString, { DayHeaderContent } from './components'
import { EventBare as Event } from './components/Event'
import { useEventPopoverCallbacks } from './eventPopover/EventPopoverProvider'
import {
  DateHeader,
  EventRow,
  ListViewScrollable,
  NoItems,
  TimeColumn,
  TimeContainer,
  TimeLabel
} from './styled'

interface ListViewProps {
  events: Model<EventDoc>[] | EventDoc[]
  dayNameFormat?: string
  userSettings?: UserSettingsDoc
  group?: Model<GroupDoc>
  groupId?: string
  groups?: GroupDoc[]
  handleAddEvent?: (startEnd: StartEndEvent) => void
  embedMode?: boolean
  loadingEvents?: boolean
}

const ListView: React.FC<ListViewProps> = React.memo(
  (props: ListViewProps) => {
    const calendar = useCalendar()
    const { timeZone } = useAppSettings()
    const calendarSync = useCalendarSync()
    const theme = useTheme()
    const { closePopover, pointTo } = useEventPopoverCallbacks()
    const scrollerRef = useRef<HTMLDivElement | null>(null)

    const { visibleAreaStart, visibleAreaEnd } = useMemo(() => {
      const range = calculateListViewRange(
        calendar.view,
        calendar.fromISO ?? '',
        timeZone
      )
      return { visibleAreaStart: range.start, visibleAreaEnd: range.end }
    }, [calendar.fromISO, calendar.view, timeZone])

    const [optimisticUpdates, setOptimisticUpdates] = React.useState<
      Map<string, Partial<EventDoc>>
    >(new Map())

    // Memoize filtered events
    const filteredEvents = useMemo(() => {
      const firstDate = visibleAreaStart.format('YYYY-MM-DD')
      const lastDate = visibleAreaEnd.format('YYYY-MM-DD')
      const today = dayjs().tz(timeZone).format('YYYY-MM-DD')
      return props.events.filter((event) => {
        const startDate = listEventDate(event, event.StartDate, timeZone)
        let endDate = listEventDate(event, eventEndTimestamp(event), timeZone)
        if (
          event.AllDay === '1' &&
          isConvertedGroupId(event.GroupID ?? '') &&
          endDate.isAfter(startDate)
        )
          endDate = endDate.subtract(1, 'day')
        return (
          (startDate.isValid() &&
            endDate.isValid() &&
            startDate.format('YYYY-MM-DD') <= lastDate &&
            endDate.format('YYYY-MM-DD') >= firstDate) ||
          (event.late && today >= firstDate && today <= lastDate) ||
          isEventRecurrenceValid(event, visibleAreaStart)
        )
      })
    }, [
      props.events,
      visibleAreaStart,
      visibleAreaEnd,
      timeZone,
      calendarSync.midnightCount
    ])

    // Defer the heavy expansion inputs so month switches keep the prior list
    // visible while React schedules a low-priority render with the new data.
    const deferredFilteredEvents = useDeferredValue(filteredEvents)
    const deferredVisibleStart = useDeferredValue(visibleAreaStart)
    const deferredVisibleEnd = useDeferredValue(visibleAreaEnd)

    // Build expanded events array (recurrences etc.) directly — no intermediate state
    const eventsForListView = useMemo(() => {
      if (!calendar.fromISO || !calendar.toISO) {
        return []
      }
      return buildEventsArrayForUIFromDBEventsWithRecurrentEvents(
        deferredFilteredEvents,
        deferredVisibleStart,
        deferredVisibleEnd
      )
    }, [
      deferredFilteredEvents,
      deferredVisibleStart,
      deferredVisibleEnd,
      calendar.fromISO,
      calendar.toISO
    ])

    // Combine real events with optimistic updates
    const eventsWithOptimisticUpdates = useMemo(() => {
      return eventsForListView.map((event) => {
        const optimisticUpdate = optimisticUpdates.get(
          event._id || event.id || ''
        )
        // Watermelon Models expose their columns via getters defined on the
        // prototype that read from a shared, mutated `_raw`. When a title (or
        // any other column) is edited, the same Model instance is reused but
        // its `_raw` is patched in place. Downstream React.memo comparators
        // would then read identical values for prev and next props and bail
        // out, leaving the UI stale. Snapshot each Model into a plain object
        // so each render carries its own captured values.
        const isModel = !!(event as Model<EventDoc>)?._raw
        const snapshot: EventDoc = isModel
          ? ({
              ...(Database.toServerObject(
                event as Model<EventDoc>
              ) as EventDoc),
              // toServerObject strips `id`, but it's used by drag handlers
              // (localEventByLocalId, eventIdForContainer) further down.
              id: (event as Model<EventDoc>).id,
              // Transient/non-DB fields that aren't part of _raw.
              late: event.late,
              lateOriginalStartTime: event.lateOriginalStartTime,
              lateOriginalEndTime: event.lateOriginalEndTime,
              referenceGroup: event.referenceGroup
            } as EventDoc)
          : (event as EventDoc)
        return optimisticUpdate
          ? { ...snapshot, ...optimisticUpdate }
          : snapshot
      })
    }, [eventsForListView, optimisticUpdates])

    // Memoize dates array
    const uniqueDates = useMemo(() => {
      let currentDate = dayjs.utc(visibleAreaStart.format('YYYY-MM-DD'))
      const endDate = dayjs.utc(visibleAreaEnd.format('YYYY-MM-DD'))
      const dates = []

      while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
        dates.push(currentDate.format('YYYY-MM-DD'))
        currentDate = currentDate.add(1, 'day')
      }

      return dates
    }, [visibleAreaStart, visibleAreaEnd])

    // Build date→events map in a single O(N) pass instead of O(dates × N).
    // Mirrors isEventInstanceWithinTimePeriod logic to determine which days each
    // event belongs to, then buckets it directly rather than re-filtering per day.
    const getEventsForDate = useMemo(() => {
      const eventsMap = new Map<string, EventDoc[]>()
      uniqueDates.forEach((date) => eventsMap.set(date, []))
      const dateSet = new Set(uniqueDates)
      const todayStr = dayjs().tz(timeZone).format('YYYY-MM-DD')

      eventsWithOptimisticUpdates.forEach((event) => {
        // Late events only appear on today
        if (event.late) {
          if (dateSet.has(todayStr)) eventsMap.get(todayStr)!.push(event)
          return
        }

        // Pending (unsaved) events: match by local date of StartDate
        if (event._id?.length === 0) {
          const dateStr = listEventDate(
            event,
            event.StartDate,
            timeZone
          ).format('YYYY-MM-DD')
          if (dateSet.has(dateStr)) eventsMap.get(dateStr)!.push(event)
          return
        }

        // Timed events follow the app zone; all-day events retain their stored dates.
        let itemStartDate = listEventDate(event, event.StartDate, timeZone)
        const itemEndDate = listEventDate(
          event,
          eventEndTimestamp(event),
          timeZone
        ).subtract(
          isConvertedGroupId(event.GroupID ?? '') &&
            event.AllDay === '1' &&
            Number(eventEndTimestamp(event)) > Number(event.StartDate)
            ? 1
            : 0,
          'day'
        )
        // iOS bug: all-day events stored at device-local midnight instead of UTC
        if (
          event.AllDay === '1' &&
          (event.TimeZoneNameID?.length === 0 || event.TimeZoneNameID !== 'UTC')
        ) {
          itemStartDate = itemStartDate.startOf('day').add(13, 'hour')
        }

        // Add to every day the event spans
        let current = dayjs.utc(itemStartDate.format('YYYY-MM-DD'))
        const endDay = dayjs.utc(itemEndDate.format('YYYY-MM-DD'))
        while (current.isSameOrBefore(endDay)) {
          const dateStr = current.format('YYYY-MM-DD')
          if (dateSet.has(dateStr)) eventsMap.get(dateStr)!.push(event)
          current = current.add(1, 'day')
        }
      })

      // Sort each day's bucket once
      uniqueDates.forEach((date) => eventsMap.get(date)?.sort(eventComparator))

      return (date: string) => eventsMap.get(date) || []
    }, [
      eventsWithOptimisticUpdates,
      uniqueDates,
      calendarSync.midnightCount,
      timeZone
    ])

    const onDragEnd = useCallback(
      (result: DropResult) => {
        LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
        setTimeout(() => {
          LocalStorage.remove(LocalStorageKey.DRAGGING)
        }, 25)

        if (!result.destination) return
        const newPositionIndex = result.destination?.index ?? 0

        const currentEvent = getEventsForDate(result.source.droppableId)[
          result.source.index
        ]

        const oldStart = currentEvent.StartDate
        const oldEnd = currentEvent.EndDate
        const oldAllday = currentEvent.AllDay
        const oldRank = currentEvent.Rank

        const startTime = listEventDate(
          currentEvent,
          currentEvent.StartDate,
          timeZone
        )

        let prevEvent =
          newPositionIndex === 0
            ? undefined
            : getEventsForDate(result.destination?.droppableId)[
                newPositionIndex
              ]
        let nextEvent: Model<EventDoc> | EventDoc | undefined =
          getEventsForDate(result.destination?.droppableId)[
            newPositionIndex + 1
          ]
        const duration =
          Number(eventEndTimestamp(currentEvent)) -
          Number(currentEvent.StartDate)

        if (result.destination?.droppableId !== result.source.droppableId) {
          prevEvent =
            newPositionIndex === 0
              ? undefined
              : getEventsForDate(result.destination?.droppableId)[
                  newPositionIndex - 1
                ]
          nextEvent = getEventsForDate(result.destination?.droppableId)[
            newPositionIndex
          ]
        }

        let rank: string
        const destinationTime = `${result.destination.droppableId} ${startTime.format('HH:mm')}`
        let newStartDate: Dayjs
        try {
          newStartDate = dayjs.tz(
            destinationTime,
            currentEvent.AllDay === '1'
              ? currentEvent.TimeZoneNameID || timeZone
              : timeZone
          )
        } catch {
          newStartDate = dayjs.tz(destinationTime, timeZone)
        }
        let allDay: string = currentEvent.AllDay ?? '0'

        if (prevEvent || nextEvent) {
          if (newPositionIndex === 0 && nextEvent) {
            rank = (Number(nextEvent?.Rank) - 100).toString()

            const nextDayStart = dayjs(
              Number(nextEvent.lateOriginalStartTime ?? nextEvent.StartDate) *
                1000
            )
            newStartDate = nextDayStart
            allDay = nextEvent.AllDay ?? '0'
          } else if (!nextEvent && prevEvent) {
            //we dropped to the end of the list
            rank = (Number(prevEvent?.Rank) + 100).toString()

            const prevEventStart = dayjs(
              Number(prevEvent.lateOriginalStartTime ?? prevEvent.StartDate) *
                1000
            )
            newStartDate = prevEventStart
            allDay = prevEvent.AllDay ?? '0'
          } else if (prevEvent && nextEvent) {
            if (prevEvent.StartDate === nextEvent.StartDate) {
              //we dropped inbetween
              rank = (
                (Number(nextEvent.Rank) + Number(prevEvent.Rank)) /
                2
              ).toString()
            } else {
              rank = (Number(prevEvent.Rank) + 100).toString()
            }

            const prevEventStart = dayjs(
              Number(prevEvent.lateOriginalStartTime ?? prevEvent.StartDate) *
                1000
            )
            newStartDate = prevEventStart
            allDay = prevEvent.AllDay ?? '0'
          }
        }

        const eventId = currentEvent._id || currentEvent.id || ''

        setTimeout(() => {
          if (
            LocalStorage.get(LocalStorageKey.PENDING_EVENT_DATA) !== null ||
            currentEvent._id?.length === 0
          ) {
            const newId =
              eventIdForContainer(currentEvent, newStartDate.valueOf()) ?? ''

            pointTo(document.getElementById(newId), 'draggable')
          }
        }, 50)

        if (currentEvent.someday === '1') {
          LocalStorage.set(
            LocalStorageKey.SOMEDAY_DATE,
            newStartDate.unix().toString()
          )
          LocalStorage.set(LocalStorageKey.SOMEDAY_ALL_DAY, allDay)
          return
        }

        if (LocalStorage.get(LocalStorageKey.VISIBLE_EVENT) === '') {
          LocalStorage.set(
            LocalStorageKey.VISIBLE_EVENT,
            `${currentEvent._id}${newStartDate.valueOf()}`
          )
        }

        // 1. IMMEDIATE UI UPDATE (Optimistic)
        setOptimisticUpdates((prev) =>
          new Map(prev).set(eventId, {
            StartDate: newStartDate.unix().toString(),
            EndDate: newStartDate.add(duration, 'second').unix().toString(),
            Rank: rank,
            AllDay: allDay
          })
        )

        if (currentEvent) {
          Database.write(async () => {
            ;(await localEventByLocalId(currentEvent.id ?? '')).update(
              (local) => {
                local.StartDate = newStartDate.unix().toString()
                local.EndDate = newStartDate
                  .add(duration, 'second')
                  .unix()
                  .toString()
                local.Rank = rank
                local.AllDay = allDay
              }
            )
          }).then(() => {
            // Clear optimistic update after database update
            setOptimisticUpdates((prev) => {
              const newMap = new Map(prev)
              newMap.delete(eventId)
              return newMap
            })

            if (currentEvent._id) {
              updateEventOnServer(
                currentEvent,
                {
                  StartDate: newStartDate.unix().toString(),
                  EndDate: newStartDate
                    .add(duration, 'second')
                    .unix()
                    .toString(),
                  Rank: rank,
                  SupplementaryGroupsIDs: currentEvent.SupplementaryGroupsIDs
                },
                true
              ).catch((error) => {
                console.error('Error updating event on server:', error)

                // Revert optimistic update on error
                setOptimisticUpdates((prev) => {
                  const newMap = new Map(prev)
                  newMap.delete(eventId)
                  return newMap
                })
                Database.write(async () => {
                  ;(await localEventById(currentEvent._id ?? '')).update(
                    (local) => {
                      local.StartDate = oldStart
                      local.EndDate = oldEnd
                      local.Rank = oldRank
                      local.AllDay = oldAllday
                    }
                  )
                })
              })
            }
          })
        }
      },
      [getEventsForDate, setOptimisticUpdates, pointTo, timeZone]
    )
    // Memoize rendered dates
    const renderedDates = useMemo(() => {
      return uniqueDates
        .map((date, index) => {
          const eventsForDate = getEventsForDate(date)

          if (eventsForDate.length === 0) return null

          return (
            <Droppable key={date} droppableId={date}>
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ width: '100%' }}
                >
                  <div>
                    <DateHeader
                      id={date}
                      isFirst={index === 0}
                      isToday={
                        date === dayjs().tz(timeZone).format('YYYY-MM-DD')
                      }
                    >
                      <DayHeaderContent
                        date={dayjs.tz(date, timeZone).toDate()}
                        groupId={props.group?._id}
                        group={props.group}
                        userSettings={props.userSettings}
                        dayNameFormat={props.dayNameFormat}
                        handleAddEvent={props.handleAddEvent}
                      />
                    </DateHeader>
                    {eventsForDate.map((event, eventIndex) => {
                      return (
                        <MemoizedEventItem
                          // Include `_rev` so Watermelon mutations (title,
                          // dates, etc.) remount this item. The event prop is
                          // the same Model instance across updates, so without
                          // _rev React.memo's shallow compare would short
                          // circuit and the UI would stay stale.
                          key={`${event.id ?? event._id}${event.StartDate}${date}${event._rev ?? ''}`}
                          event={event}
                          eventIndex={eventIndex}
                          date={date}
                          timeZone={timeZone}
                        />
                      )
                    })}
                  </div>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )
        })
        .filter(Boolean)
    }, [
      uniqueDates,
      getEventsForDate,
      props.group,
      props.userSettings,
      props.dayNameFormat,
      props.handleAddEvent,
      timeZone
    ])

    useEffect(() => {
      const isFullyVisibleInScroller = (element: HTMLElement) => {
        const scroller = scrollerRef.current

        if (!scroller) {
          const rect = element.getBoundingClientRect()
          return rect.top >= 0 && rect.bottom <= window.innerHeight
        }

        const elementRect = element.getBoundingClientRect()
        const scrollerRect = scroller.getBoundingClientRect()

        return (
          elementRect.top >= scrollerRect.top &&
          elementRect.bottom <= scrollerRect.bottom
        )
      }

      const pendingItem = document.getElementById('pending')

      if (pendingItem) {
        if (!isFullyVisibleInScroller(pendingItem)) {
          pendingItem.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          })
        }
        return
      }

      const element = document.getElementById(
        calendar.scrollToTime.format('YYYY-MM-DD') ?? ''
      )
      if (element) {
        if (!isFullyVisibleInScroller(element)) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          })
        }
      }
    }, [renderedDates, calendar.scrollToTime])

    return (
      <DragDropContext
        key={'dnd-list'}
        onBeforeDragStart={() => {
          LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
          closePopover()
        }}
        onDragStart={() => {
          LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
          LocalStorage.set(LocalStorageKey.DRAGGING, '1')
        }}
        onDragEnd={onDragEnd}
      >
        <ListViewScrollable
          className="fc-scroller"
          ref={scrollerRef}
          style={{
            backgroundColor: theme.palette.background.default
          }}
        >
          {eventsForListView.length === 0 && !props.loadingEvents && (
            <NoItems key={0}>{t('nothingOnYourAgenda')}</NoItems>
          )}
          {renderedDates}

          <Box height={80} />
        </ListViewScrollable>
      </DragDropContext>
    )
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    return (
      prevProps.events === nextProps.events &&
      prevProps.dayNameFormat === nextProps.dayNameFormat &&
      prevProps.userSettings === nextProps.userSettings &&
      prevProps.group?._id === nextProps.group?._id &&
      prevProps.groupId === nextProps.groupId &&
      prevProps.loadingEvents === nextProps.loadingEvents &&
      prevProps.embedMode === nextProps.embedMode
    )
  }
)

// Create a memoized event item component
const MemoizedEventItem = React.memo(
  ({
    event,
    eventIndex,
    date,
    timeZone
  }: {
    event: EventDoc
    eventIndex: number
    date: string
    timeZone: string
  }) => {
    const [contextMenuPosition, setContextMenuPosition] = React.useState<{
      x: number
      y: number
    } | null>(null)
    const [selectedEventForMenu, setSelectedEventForMenu] = React.useState<{
      event: EventDoc
      instanceStart: number
      instanceEnd: number
    } | null>(null)

    const handleContextMenu = (
      e: React.MouseEvent,
      eventDoc: EventDoc,
      instanceStart: number,
      instanceEnd: number
    ) => {
      e.preventDefault()
      setContextMenuPosition({ x: e.clientX, y: e.clientY })
      setSelectedEventForMenu({
        event: eventDoc,
        instanceStart,
        instanceEnd
      })
    }

    const handleCloseContextMenu = () => {
      setContextMenuPosition(null)
      setSelectedEventForMenu(null)
    }

    const { start, end, longLabel, uniqueId } = useMemo(() => {
      const startDate = listEventDate(event, event.StartDate, timeZone)
      const endDate = listEventDate(event, eventEndTimestamp(event), timeZone)
      const startDay = dayjs.utc(startDate.format('YYYY-MM-DD'))
      const endDay = dayjs.utc(endDate.format('YYYY-MM-DD'))
      const lengthDays = endDay.diff(startDay, 'day') + 1
      let dayNum = dayjs.utc(date).diff(startDay, 'day') + 1
      if (isConvertedGroupId(event.GroupID) && event.AllDay === '1') {
        dayNum += 1
      }

      const longLabel =
        lengthDays > 1
          ? `, (${t('day_of_long_event', { num: dayNum, total: lengthDays })})`
          : undefined

      let start = event.late
        ? dayjs(Number(event.lateOriginalStartTime) * 1000)
            .tz(timeZone)
            .format('MMM D YYYY')
        : dateToTimeString(startDate.toDate(), true, false, timeZone)
      let end = event.late
        ? dayjs(Number(event.lateOriginalEndTime) * 1000)
            .tz(timeZone)
            .format('MMM D YYYY')
        : dateToTimeString(endDate.toDate(), true, false, timeZone)

      if (lengthDays > 1) {
        if (date === startDate.format('YYYY-MM-DD')) {
          start = dateToTimeString(startDate.toDate(), true, false, timeZone)
          end = endDate.format('MMM D')
        } else if (date === endDate.format('YYYY-MM-DD')) {
          start = startDate.format('MMM D')
          end = dateToTimeString(endDate.toDate(), true, false, timeZone)
        } else {
          start = startDate.format('MMM D')
          end = endDate.format('MMM D')
        }

        if (event.AllDay === '1' && !event.late) {
          start = startDate.format('MMM D')
          end = endDate
            .add(isConvertedGroupId(event.GroupID ?? '') ? -1 : 0, 'day')
            .format('MMM D')
        }
      } else if (event.AllDay === '1' && !event.late) {
        start = t('allDay')
        end = ''
      }

      const uniqueId = `${event.id ?? event._id}${event.StartDate}${date}`
      return { start, end, longLabel, uniqueId }
    }, [
      event.id,
      event._id,
      event.StartDate,
      event.EndDate,
      event.AllDay,
      event.TimeZoneNameID,
      event.GroupID,
      event.late,
      event.lateOriginalStartTime,
      event.lateOriginalEndTime,
      date,
      timeZone
    ])
    return (
      <>
        {contextMenuPosition && selectedEventForMenu && (
          <EventContextMenu
            x={contextMenuPosition.x}
            y={contextMenuPosition.y}
            onClose={handleCloseContextMenu}
            event={selectedEventForMenu.event}
            group={undefined}
            instanceStart={selectedEventForMenu.instanceStart}
            instanceEnd={selectedEventForMenu.instanceEnd}
          />
        )}
        <Draggable
          isDragDisabled={
            isGuest() ||
            (event.Recurrence !== undefined &&
              event.Recurrence !== null &&
              typeof event.Recurrence !== 'string')
          }
          key={uniqueId}
          draggableId={uniqueId}
          index={eventIndex}
        >
          {(provided) => {
            return (
              <div
                data-unique-id={uniqueId}
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                onContextMenu={(e) => {
                  if (event._id?.length === 0 || isGuest()) return
                  handleContextMenu(
                    e,
                    event,
                    Number(event.StartDate) * 1000,
                    Number(eventEndTimestamp(event)) * 1000
                  )
                }}
              >
                <EventRow
                  id={event._id?.length === 0 ? 'pending' : undefined}
                  key={`${event._id}${event.StartDate}`}
                >
                  <TimeColumn>
                    <TimeContainer>
                      <TimeLabel
                        sx={{
                          color: event.late
                            ? 'var(--color-negative)'
                            : undefined
                        }}
                      >
                        {start}
                      </TimeLabel>
                      {start !== end && !event.late ? (
                        <TimeLabel>{end}</TimeLabel>
                      ) : (
                        <></>
                      )}
                    </TimeContainer>
                  </TimeColumn>
                  <Event
                    alwaysFullSize
                    isStart
                    instanceStart={Number(event.StartDate) * 1000}
                    instanceEnd={
                      (Number(eventEndTimestamp(event)) -
                        (event.AllDay === '1' &&
                        (event._id?.length ?? 0) > 0 &&
                        event.StartDate != eventEndTimestamp(event) &&
                        isConvertedGroupId(event.GroupID ?? '')
                          ? 12 * 60 * 60
                          : 0)) *
                      1000
                    }
                    event={event}
                    title={`${event.Text}${longLabel ? longLabel : ''}`}
                  />
                </EventRow>
              </div>
            )
          }}
        </Draggable>
      </>
    )
  },
  // Watermelon mutates the same Model instance in place for local edits, and
  // `_rev` only updates on server round-trips. Without a custom comparator,
  // React.memo's shallow check on `event` short-circuits and the UI keeps the
  // pre-edit title/dates. Compare the fields actually rendered.
  (prevProps, nextProps) => {
    return (
      prevProps.eventIndex === nextProps.eventIndex &&
      prevProps.date === nextProps.date &&
      prevProps.timeZone === nextProps.timeZone &&
      prevProps.event._id === nextProps.event._id &&
      prevProps.event.id === nextProps.event.id &&
      prevProps.event._rev === nextProps.event._rev &&
      prevProps.event.Text === nextProps.event.Text &&
      prevProps.event.StartDate === nextProps.event.StartDate &&
      prevProps.event.EndDate === nextProps.event.EndDate &&
      prevProps.event.AllDay === nextProps.event.AllDay &&
      prevProps.event.TimeZoneNameID === nextProps.event.TimeZoneNameID &&
      prevProps.event.Color === nextProps.event.Color &&
      prevProps.event.GroupID === nextProps.event.GroupID &&
      prevProps.event.Status === nextProps.event.Status &&
      prevProps.event.late === nextProps.event.late &&
      prevProps.event.OwnerID === nextProps.event.OwnerID &&
      prevProps.event.PersonalOwnerId === nextProps.event.PersonalOwnerId
    )
  }
)

export default ListView

export function calculateListViewRange(
  viewType: CalendarRange,
  dateStr: string,
  timeZone = getDefaultTimezone()
) {
  const parsed = dayjs(dateStr)
  if (!parsed.isValid()) return { start: parsed, end: parsed }
  const date = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
    ? dayjs.tz(dateStr, timeZone)
    : parsed.tz(timeZone)
  const calendarDate = dayjs.utc(date.format('YYYY-MM-DD'))
  if (viewType === CalendarRange.LISTDAY) {
    return {
      start: dayjs.tz(
        `${calendarDate.format('YYYY-MM-DD')} 00:00:00`,
        timeZone
      ),
      end: dayjs
        .tz(
          `${calendarDate.add(1, 'day').format('YYYY-MM-DD')} 00:00:00`,
          timeZone
        )
        .subtract(1, 'millisecond')
    }
  }

  // Keep the existing month padding; resolve each boundary's own DST offset.
  const startDate = calendarDate.startOf('month').subtract(1, 'day')
  const endDate = startDate.add(1, 'month').add(4, 'day')
  const start = dayjs.tz(startDate.format('YYYY-MM-DD'), timeZone)
  const end = dayjs.tz(endDate.format('YYYY-MM-DD'), timeZone)

  return { start, end }
}
